import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import { useTranslation } from 'react-i18next';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Label,
  UncontrolledDropdown,
} from 'reactstrap';

import translations from 'services/translations/translations';
import { offerTextToDelta } from 'services/offers/offerText';
import registerOfferTextFormats, {
  OFFER_TEXT_FORMATS,
} from 'services/offers/registerOfferTextFormats';

import 'react-quill/dist/quill.snow.css';
import 'style/offers/RichOfferTextEditor.css';

registerOfferTextFormats();

const PALETTE = [
  '#000000',
  '#595959',
  '#a6a6a6',
  '#4d854a',
  '#9fd03f',
  '#c00000',
  '#ff0000',
  '#ffc000',
  '#ffff00',
  '#0070c0',
];

const LIST_STYLES: {
  key: string;
  symbol: string;
  list: string;
  marker: string | false;
}[] = [
  { key: 'dot', symbol: '•', list: 'bullet', marker: false },
  { key: 'dash', symbol: '–', list: 'bullet', marker: 'dash' },
  { key: 'arrow', symbol: '→', list: 'bullet', marker: 'arrow' },
  { key: 'circle', symbol: '◦', list: 'bullet', marker: 'circle' },
  { key: 'square', symbol: '▪', list: 'bullet', marker: 'square' },
  { key: 'numbered', symbol: '1.', list: 'ordered', marker: false },
];

const MAX_INDENT = 8;

// Keeps the editor selection when a toolbar button is clicked
const keepSelection = (event: React.MouseEvent) => event.preventDefault();

const ToolbarButton = ({
  active,
  children,
  onClick,
  title,
}: {
  active?: boolean;
  children: React.ReactNode;
  onClick: () => void;
  title: string;
}): JSX.Element => (
  <button
    type="button"
    className={`rich-text-button${active ? ' active' : ''}`}
    onMouseDown={keepSelection}
    onClick={onClick}
    title={title}
    aria-label={title}
    aria-pressed={active}
  >
    {children}
  </button>
);

const ColorPicker = ({
  color,
  icon,
  noColorLabel,
  onChange,
  title,
}: {
  color: string | undefined;
  icon: React.ReactNode;
  noColorLabel?: string;
  onChange: (color: string | false) => void;
  title: string;
}): JSX.Element => (
  <UncontrolledDropdown className="rich-text-color-picker">
    <DropdownToggle
      caret
      className="rich-text-button"
      onMouseDown={keepSelection}
      title={title}
      aria-label={title}
    >
      <span className="rich-text-color-icon">
        {icon}
        <span
          className="rich-text-color-bar"
          style={{ backgroundColor: color ?? 'transparent' }}
        />
      </span>
    </DropdownToggle>
    <DropdownMenu>
      <div className="rich-text-palette">
        {PALETTE.map((paletteColor) => (
          <DropdownItem
            key={paletteColor}
            className="rich-text-swatch"
            onMouseDown={keepSelection}
            onClick={() => onChange(paletteColor)}
            style={{ backgroundColor: paletteColor }}
            title={paletteColor}
            aria-label={paletteColor}
          />
        ))}
      </div>
      <div className="rich-text-custom-color">
        <input
          type="color"
          value={color ?? '#000000'}
          onChange={(event) => onChange(event.target.value)}
          title={title}
          aria-label={title}
        />
      </div>
      {noColorLabel === undefined ? null : (
        <DropdownItem
          onMouseDown={keepSelection}
          onClick={() => onChange(false)}
        >
          {noColorLabel}
        </DropdownItem>
      )}
    </DropdownMenu>
  </UncontrolledDropdown>
);

const RichOfferTextEditor = ({
  disabled,
  label,
  onChange,
  tall,
  value,
  wide,
}: {
  disabled: boolean;
  label: string;
  onChange: (value: string) => void;
  // A text printed on a page of its own (e.g. the delivery terms) gets room like a page
  tall?: boolean;
  value: string | null;
  // The general offer text takes the full page width in the PDF, a position text a column
  wide?: boolean;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const quillRef = useRef<ReactQuill>(null);
  const lastEmitted = useRef<string | null>(value);
  const [formats, setFormats] = useState<{ [format: string]: any }>({});
  const [strikeColor, setStrikeColor] = useState('#c00000');

  const initialValue = useMemo(() => offerTextToDelta(value) as any, []);

  const getEditor = () => quillRef.current?.getEditor();

  // Another version of the product was selected or the text was changed elsewhere
  useEffect(() => {
    const editor = getEditor();
    if (editor === undefined || value === lastEmitted.current) {
      return;
    }

    lastEmitted.current = value;
    editor.setContents(offerTextToDelta(value) as any, 'silent');
  }, [value]);

  const updateFormats = () => {
    const editor = getEditor();
    const range = editor?.getSelection();
    setFormats(editor !== undefined && range ? editor.getFormat(range) : {});
  };

  const handleChange = (_content: string, _delta: unknown, source: string) => {
    const editor = getEditor();
    if (editor === undefined || source === 'silent') {
      return;
    }

    const next =
      editor.getText().trim() === ''
        ? ''
        : JSON.stringify(editor.getContents());

    if (next !== lastEmitted.current) {
      lastEmitted.current = next;
      onChange(next);
    }

    updateFormats();
  };

  const format = (name: string, formatValue: any) => {
    const editor = getEditor();
    if (editor === undefined) {
      return;
    }

    editor.focus();
    editor.format(name, formatValue, 'user');
    updateFormats();
  };

  const toggle = (name: string) => format(name, !formats[name]);

  const setListStyle = (listStyle: (typeof LIST_STYLES)[number]) => {
    const isActive =
      formats.list === listStyle.list &&
      (formats.marker ?? false) === listStyle.marker;

    if (isActive) {
      format('list', false);
      format('marker', false);
      return;
    }

    format('list', listStyle.list);
    format('marker', listStyle.marker);
  };

  const changeIndent = (change: number) => {
    const indent = Math.min(
      MAX_INDENT,
      Math.max(0, (parseInt(formats.indent, 10) || 0) + change)
    );
    format('indent', indent === 0 ? false : indent);
  };

  const toggleStrike = () =>
    format('strikecolor', formats.strikecolor ? false : strikeColor);

  const changeStrikeColor = (color: string | false) => {
    const newColor = color === false ? '#000000' : color;
    setStrikeColor(newColor);
    format('strikecolor', newColor);
  };

  const clearFormatting = () => {
    const editor = getEditor();
    const range = editor?.getSelection(true);
    if (editor === undefined || !range) {
      return;
    }

    editor.removeFormat(range.index, range.length, 'user');
    updateFormats();
  };

  const history = (action: 'undo' | 'redo') => {
    const editor: any = getEditor();
    editor?.history[action]();
    updateFormats();
  };

  const activeListStyle = LIST_STYLES.find(
    (listStyle) =>
      formats.list === listStyle.list &&
      (formats.marker ?? false) === listStyle.marker
  );

  const tr = (key: string) => t(`${translations.offers}:richText.${key}`);

  return (
    <div
      className={`rich-offer-text${disabled ? ' disabled' : ''}${
        wide ? ' wide' : ''
      }${tall ? ' tall' : ''}`}
    >
      <Label className="label">{label}</Label>
      {disabled ? null : (
        <div className="rich-text-toolbar" role="toolbar" aria-label={label}>
          <div className="rich-text-group">
            <ToolbarButton
              active={formats.bold}
              onClick={() => toggle('bold')}
              title={tr('Bold')}
            >
              <strong>B</strong>
            </ToolbarButton>
            <ToolbarButton
              active={formats.italic}
              onClick={() => toggle('italic')}
              title={tr('Italic')}
            >
              <em>I</em>
            </ToolbarButton>
            <ToolbarButton
              active={formats.underline}
              onClick={() => toggle('underline')}
              title={tr('Underline')}
            >
              <u>U</u>
            </ToolbarButton>
            <ToolbarButton
              active={Boolean(formats.strikecolor)}
              onClick={toggleStrike}
              title={tr('Strikethrough')}
            >
              <span
                style={{
                  textDecoration: 'line-through',
                  textDecorationColor: strikeColor,
                }}
              >
                S
              </span>
            </ToolbarButton>
            <ColorPicker
              color={formats.strikecolor ?? strikeColor}
              icon={<span className="rich-text-strike-icon">S</span>}
              onChange={changeStrikeColor}
              title={tr('Strikethrough color')}
            />
          </div>

          <div className="rich-text-group">
            <ColorPicker
              color={formats.color}
              icon={<span>A</span>}
              noColorLabel={tr('Default color')}
              onChange={(color) => format('color', color)}
              title={tr('Text color')}
            />
            <ColorPicker
              color={formats.background}
              icon={<span className="rich-text-highlight-icon">A</span>}
              noColorLabel={tr('No highlight')}
              onChange={(color) => format('background', color)}
              title={tr('Highlight')}
            />
            <UncontrolledDropdown className="rich-text-dropdown">
              <DropdownToggle
                caret
                className="rich-text-button"
                onMouseDown={keepSelection}
                title={tr('Size')}
              >
                {tr(`sizes.${formats.size ?? 'normal'}`)}
              </DropdownToggle>
              <DropdownMenu>
                {['small', 'normal', 'large'].map((size) => (
                  <DropdownItem
                    key={size}
                    active={(formats.size ?? 'normal') === size}
                    onMouseDown={keepSelection}
                    onClick={() =>
                      format('size', size === 'normal' ? false : size)
                    }
                  >
                    {tr(`sizes.${size}`)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>

          <div className="rich-text-group">
            <UncontrolledDropdown className="rich-text-dropdown">
              <DropdownToggle
                caret
                className={`rich-text-button${
                  activeListStyle ? ' active' : ''
                }`}
                onMouseDown={keepSelection}
                title={tr('List')}
              >
                {activeListStyle ? activeListStyle.symbol : '•'} {tr('List')}
              </DropdownToggle>
              <DropdownMenu>
                {LIST_STYLES.map((listStyle) => (
                  <DropdownItem
                    key={listStyle.key}
                    active={activeListStyle?.key === listStyle.key}
                    onMouseDown={keepSelection}
                    onClick={() => setListStyle(listStyle)}
                  >
                    <span className="rich-text-list-symbol">
                      {listStyle.symbol}
                    </span>
                    {tr(`lists.${listStyle.key}`)}
                  </DropdownItem>
                ))}
                <DropdownItem divider />
                <DropdownItem
                  disabled={activeListStyle === undefined}
                  onMouseDown={keepSelection}
                  onClick={() => {
                    format('list', false);
                    format('marker', false);
                  }}
                >
                  {tr('No list')}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            <ToolbarButton
              onClick={() => changeIndent(-1)}
              title={tr('Decrease indent')}
            >
              ⇤
            </ToolbarButton>
            <ToolbarButton
              onClick={() => changeIndent(1)}
              title={tr('Increase indent')}
            >
              ⇥
            </ToolbarButton>
          </div>

          <div className="rich-text-group">
            {[
              { value: false, key: 'left' },
              { value: 'center', key: 'center' },
              { value: 'right', key: 'right' },
              { value: 'justify', key: 'justify' },
            ].map((alignment) => (
              <ToolbarButton
                key={alignment.key}
                active={(formats.align ?? false) === alignment.value}
                onClick={() => format('align', alignment.value)}
                title={tr(`align.${alignment.key}`)}
              >
                <span className={`rich-text-align-icon align-${alignment.key}`}>
                  <span />
                  <span />
                  <span />
                </span>
              </ToolbarButton>
            ))}
          </div>

          <div className="rich-text-group">
            <ToolbarButton
              onClick={clearFormatting}
              title={tr('Clear formatting')}
            >
              T<sub>x</sub>
            </ToolbarButton>
            <ToolbarButton onClick={() => history('undo')} title={tr('Undo')}>
              ↶
            </ToolbarButton>
            <ToolbarButton onClick={() => history('redo')} title={tr('Redo')}>
              ↷
            </ToolbarButton>
          </div>
        </div>
      )}
      <ReactQuill
        ref={quillRef}
        className="rich-text-editor"
        defaultValue={initialValue}
        formats={OFFER_TEXT_FORMATS}
        modules={{ toolbar: false }}
        onChange={handleChange}
        onChangeSelection={updateFormats}
        readOnly={disabled}
        theme="snow"
      />
    </div>
  );
};

export default RichOfferTextEditor;
